import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { OutletContext } from '../../layout/Layout';
import PageTitleSubheader from '../../layout/subheader/PageTitleSubheader';
import {
	NotificationDrawer,
	NotificationDrawerHeader,
	Spinner,
	Bullseye,
	Button,
	Grid,
	Card,
	CardBody,
	GridItem,
	ExpandableSection,
	Tooltip,
} from '@patternfly/react-core';
import './Notifications.scss';
import { useUser } from '../../components/user/ApplicationProvider';
import { User } from '../../api/security/User';
import { TAppNotification, TJobDetails } from '../../api/types/AppNotification';
import { faEnvelopeCircleCheck, faRefresh, faTrash, faX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	dismissAllNotifications,
	markAllNotificationsAsRead,
} from '../../api/notification/Notification';
import NotificationsGroup from './NotificationsGroup';
import { Table, Thead, Tr, Th, Tbody, Td } from '@patternfly/react-table';
import { useMount } from 'react-use';
import { Documents, TDocuments } from '../../api/documents/Documents';
import { DocumentDownloadActions } from './DocumentDownloadActions';

const Notifications = () => {
	const navigate = useNavigate();
	const { setSubSide, subNavExpanded, setSubNavExpanded } = useOutletContext<OutletContext>();
	const [searchTerm, setSearchTerm] = useState<string>();
	const user = useUser();
	const [notifications, setNotifications] = useState<TAppNotification[]>([]);
	const [notificationsLoaded, setNotificationsLoaded] = useState(false);
	const [unreadCount, setUnreadCount] = useState<number>(0);
	const [isGroupExpanded, setIsGroupExpanded] = useState(false);
	const [documents, setDocuments] = useState<TDocuments[]>([]);
	const [isLoadingDocuments, setIsLoadingDocuments] = useState<boolean>(false);
	const [selectedDocs, setSelectedDocs] = useState<number[]>([]);

	const onOpen = () => {
		setIsGroupExpanded((prev) => !prev);
	};

	useMount(() => {
		getAllDocuments();
	});

	const getAllDocuments = () => {
		setIsLoadingDocuments(true);
		setDocuments([]);

		setTimeout(() => {
			void Documents.GetAll().then((response) => {
				setDocuments(response);
				setIsLoadingDocuments(false);
			});
		}, 500);
	};

	useEffect(() => {
		setSubSide({
			subheaderContext: (
				<PageTitleSubheader
					pageTitle="Notifications"
					expanded={subNavExpanded}
					setExpanded={setSubNavExpanded}
				/>
			),
			sidebarContext: null,
			hideLeftSideBar: true,
		});
	}, [setSubSide, subNavExpanded, setSubNavExpanded]);

	useEffect(() => {
		if (notificationsLoaded) {
			setUnreadCount(notifications.filter((n) => !n.read).length);
			return;
		}

		User.GetNotifications()
			.then((notifications) => {
				notifications.map((notification) => {
					if (notification.job_details) {
						notification.job_details = JSON.parse(
							notification.job_details.toString()
						) as TJobDetails;
					}

					return notification;
				});

				setNotifications(notifications);
				setNotificationsLoaded(true);
			})
			.catch((error) => {
				console.error(error);
			});
	}, [notifications]);

	const markAllAsRead = () => {
		const newState = notifications.map((notification) => {
			notification.read = true;
			return notification;
		});

		setNotifications(newState);
		void markAllNotificationsAsRead();
	};

	const dismissAllNotificationsOnClick = () => {
		void dismissAllNotifications();
		setNotifications([]);
	};

	if (!notificationsLoaded) {
		return (
			<Bullseye style={{ paddingTop: '2rem' }}>
				<Spinner
					isSVG
					size="xl"
				/>
			</Bullseye>
		);
	}

	const groupBy = <T, K extends keyof any>(
		array: T[],
		keyGetter: (item: T) => K
	): Record<K, T[]> => {
		return array.reduce((result, currentValue) => {
			const key = keyGetter(currentValue);
			if (!result[key]) result[key] = [];
			result[key].push(currentValue);
			return result;
		}, {} as Record<K, T[]>);
	};

	const groupedNotifications = groupBy(
		notifications,
		(notification) => notification.job_details?.entityType ?? 'null'
	);

	const handleDocumentClear = (documentId: number) => {
		void Documents.Clear(documentId).then((response) => {
			getAllDocuments();
		});
	};

	const toggleSelectAll = () => {
		const allIds = documents.map((d) => d.id);
		const isAllSelected = selectedDocs.length === allIds.length;

		setSelectedDocs(isAllSelected ? [] : allIds);
	};

	const toggleOne = (id: number) => {
		setSelectedDocs((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
		);
	};

	const handleBulkDelete = (ids: number[]) => {
		ids.map((documentId) => {
			handleDocumentClear(documentId);
		});

		setSelectedDocs([]);
	};

	const isAllChecked = documents.length > 0 && selectedDocs.length === documents.length;

	return (
		<Card className="notifications-container">
			<CardBody>
				<NotificationDrawer>
					<NotificationDrawerHeader count={unreadCount}>
						<Button
							variant="plain"
							disabled={unreadCount === 0}
							title="Click to mark as read"
							onClick={() => markAllAsRead()}
						>
							<FontAwesomeIcon icon={faEnvelopeCircleCheck} />
						</Button>
						<Button
							variant="plain"
							title="Click to dismiss(Delete) all notifications"
							onClick={() => dismissAllNotificationsOnClick()}
						>
							<FontAwesomeIcon icon={faX} />
						</Button>
					</NotificationDrawerHeader>
					<Grid hasGutter>
						<GridItem span={12}>
							<ExpandableSection
								toggleText="My Documents Queue"
								onToggle={onOpen}
								isExpanded={isGroupExpanded}
								isIndented
								data-testid="Job_type-document"
							>
								<div
									style={{
										display: 'flex',
										justifyContent: 'flex-end',
										marginBottom: '8px',
									}}
								>
									{selectedDocs.length > 0 && (
										<Button
											variant="primary"
											onClick={() => handleBulkDelete(selectedDocs)}
											className="btn-docs"
										>
											Clear Selected Document(s)
										</Button>
									)}
									<Button
										variant="primary"
										onClick={getAllDocuments}
										className="btn-docs"
									>
										<FontAwesomeIcon icon={faRefresh} /> Refresh
									</Button>
								</div>
								<div className="tblMyDocs">
									<Table
										variant="compact"
										borders
									>
										<Thead>
											<Tr>
												<Th>
													<Tooltip content="Select All">
														<input
															type="checkbox"
															checked={isAllChecked}
															onChange={toggleSelectAll}
														/>
													</Tooltip>
												</Th>
												<Th>Name</Th>
												<Th>Size</Th>
												<Th>Date</Th>
												<Th>Status</Th>
												<Th>Expires</Th>
												<Th>Action</Th>
											</Tr>
										</Thead>
										<Tbody>
											{isLoadingDocuments ? (
												<Tr>
													<Td
														textCenter
														colSpan={6}
													>
														Loading...
													</Td>
												</Tr>
											) : documents.length > 0 ? (
												documents.map((doc, idx) => (
													<Tr key={idx}>
														<Td>
															<input
																type="checkbox"
																checked={selectedDocs.includes(
																	doc.id
																)}
																onChange={() => toggleOne(doc.id)}
															/>
														</Td>
														<Td>{doc.name}</Td>
														<Td>
															{doc.size ? `${doc.size} KB` : 'N/A'}
														</Td>
														<Td>{doc.date}</Td>
														<Td>{doc.status}</Td>
														<Td>{doc.expires}</Td>
														<Td>
															{doc.status === 'Completed' &&
																doc.action && (
																	<DocumentDownloadActions
																		actionUrl={doc.action.toString()}
																		file_path={doc.file_path}
																		documentId={doc.id}
																		getAllDocuments={
																			getAllDocuments
																		}
																	/>
																)}
															{doc.status != 'Completed' && (
																<Tooltip content="Clear Document">
																	<FontAwesomeIcon
																		style={{
																			cursor: 'pointer',
																		}}
																		icon={faTrash}
																		onClick={() => {
																			handleDocumentClear(
																				doc.id
																			);
																		}}
																	/>
																</Tooltip>
															)}
														</Td>
													</Tr>
												))
											) : (
												<Tr>
													<Td
														textCenter
														colSpan={6}
													>
														No documents found
													</Td>
												</Tr>
											)}
										</Tbody>
									</Table>
								</div>
							</ExpandableSection>

							{Object.entries(groupedNotifications).map(
								([jobType, groupedNotifications]) => (
									<NotificationsGroup
										jobGroup={{
											jobType: jobType,
											notifications: groupedNotifications,
										}}
										notifications={notifications}
										setNotifications={setNotifications}
										groupBy={groupBy}
									/>
								)
							)}
						</GridItem>
					</Grid>
				</NotificationDrawer>
			</CardBody>
		</Card>
	);
};

export default Notifications;
